import app from 'flarum/forum/app';
import { extend } from 'flarum/common/extend';
import ModalManagerState from 'flarum/common/states/ModalManagerState';

// The title of the composer that opened the category picker, focused once
// the picker closes - see below.
let focusOnClose = null;

/**
 * A new discussion started without a category - from the home page, not a
 * forum's own page, which preselects its tag (flarum-tags' addTagComposer.js)
 * - opens the category picker (see categorySelect.tsx) right away, rather
 * than only once its author submits: every discussion needs one, and it
 * decides where the post appears and which rules apply (see
 * showTagRulesInComposer.tsx). Dismissing it changes nothing: the submit
 * still asks, as before.
 *
 * Whoever opens the composer with a category preselects it before the
 * composer shows (see champis-events' UpcomingEvents.tsx and
 * champis-species' RandomDescriptions.tsx), so no category here means none
 * is coming. Not for fof-byobu's private discussions (its
 * PrivateDiscussionComposer extends DiscussionComposer, always with
 * `recipients`): they have none.
 */
export default function chooseCategoryOnNewDiscussion() {
  // String target: DiscussionComposer is one of core's lazy chunks, see
  // showTagRulesInComposer.tsx.
  extend('flarum/forum/components/DiscussionComposer', 'oncreate', function () {
    if (this.composer.fields.recipients) return;

    // chooseTags() only offers the tags already in the store, and only
    // when there are any the actor may pick (see flarum-tags'
    // addTagComposer.js, which also loads them in oninit).
    app.tagList.load(['parent']).then(() => {
      // Still open, still without a category, nothing else asked meanwhile.
      if (!this.element?.isConnected || (this.composer.fields.tags || []).length || app.modal.isModalOpen()) return;
      focusOnClose = this.element.querySelector('.DiscussionComposer-title');
      this.chooseTags();
    });
  });

  // Once the picker closes - chosen or dismissed - the title, what's typed
  // next. Core's focus trap hands focus back to the element focused when
  // it activated, which by then is the picker's own <select>, gone with it
  // (chooseTags() itself focuses a <textarea>, which the rich text editor
  // doesn't have): focus ends up nowhere. Hence after that hand-back -
  // ModalManager.onupdate waits a frame, focus-trap a timeout - and only
  // if it's still nowhere.
  extend(ModalManagerState.prototype, 'close', function () {
    const target = focusOnClose;
    if (!target || this.isModalOpen()) return;
    focusOnClose = null;
    setTimeout(() => {
      const focused = document.activeElement;
      if (target.isConnected && (!focused || focused === document.body || !focused.offsetParent)) {
        target.focus();
      }
    }, 200);
  });
}